import { extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import tokens from '@te-digi/styleguide-tokens';
import { getResponsiveStyle } from '../utils/styles/get-responsive-style.js';

const getColor = color => color === 'dark' ? 'neutral-7' : color;
const StyledText = styled.span.withConfig({
  componentId: "sg1390__sc-s5mj0a-0"
})(["color:", ";display:", ";font-style:", ";font-weight:", ";line-height:", ";margin-bottom:0;overflow:", ";text-overflow:", ";text-transform:", ";white-space:", ";", " ", " ", ""], _ref => {
  let {
    $color
  } = _ref;
  return $color ? tokens.color[getColor($color)] : undefined;
}, _ref2 => {
  let {
    $block
  } = _ref2;
  return $block ? 'block' : undefined;
}, _ref3 => {
  let {
    $fontStyle
  } = _ref3;
  return $fontStyle;
}, _ref4 => {
  let {
    $fontWeight
  } = _ref4;
  return $fontWeight ? tokens['font-weight'][$fontWeight] : undefined;
}, _ref5 => {
  let {
    $lineHeight
  } = _ref5;
  return $lineHeight ? tokens['line-height'][$lineHeight] : undefined;
}, props => props.$overflow, props => props.$textOverflow, _ref6 => {
  let {
    $transform
  } = _ref6;
  return $transform;
}, _ref7 => {
  let {
    $whiteSpace
  } = _ref7;
  return $whiteSpace;
}, props => getResponsiveStyle('font-size', props.$size), props => getResponsiveStyle('text-align', props.$align), props => props.$lines && css(["-webkit-box-orient:vertical;-webkit-line-clamp:", ";display:-webkit-box;hyphens:auto;line-clamp:", ";overflow:hidden;"], props.$lines, props.$lines));
const Text = /*#__PURE__*/forwardRef((_ref8, ref) => {
  let {
    align,
    as,
    block,
    children,
    className,
    color,
    fontStyle,
    fontWeight,
    id,
    lineHeight,
    lines,
    size,
    title,
    transform,
    truncate,
    whiteSpace,
    ...rest
  } = _ref8;
  return children !== undefined ? /*#__PURE__*/React__default.createElement(StyledText, _extends({
    $align: align,
    $block: block || !!align,
    $color: color,
    $fontStyle: fontStyle,
    $fontWeight: fontWeight,
    $lineHeight: lineHeight,
    $lines: lines,
    $size: size,
    $textOverflow: truncate ? 'ellipsis' : undefined,
    $transform: transform,
    $overflow: truncate ? 'hidden' : undefined,
    $whiteSpace: truncate ? 'nowrap' : whiteSpace,
    as: as,
    className: className,
    id: id,
    ref: ref,
    title: title
  }, rest), children) : null;
});
const TEXT_ALIGN = ['center', 'right', 'left'];
const TEXT_SIZE = ['sm', 'md', 'lg', 'xl', 'xxl', 'xxxl', 'xxxxl', 'xxxxxl', 'xxxxxxl'];

// TODO: Figure out why displayName and propTypes does not work with TypeScript.
// @ts-expect-error
Text.displayName = 'Text';
// @ts-expect-error
Text.propTypes = {
  align: PropTypes.oneOfType([PropTypes.oneOf(TEXT_ALIGN), PropTypes.shape({
    xs: PropTypes.oneOf(TEXT_ALIGN),
    sm: PropTypes.oneOf(TEXT_ALIGN),
    md: PropTypes.oneOf(TEXT_ALIGN),
    lg: PropTypes.oneOf(TEXT_ALIGN),
    xl: PropTypes.oneOf(TEXT_ALIGN)
  })]),
  /**
   * Element or component used for the root node.
   */
  as: PropTypes.elementType,
  block: PropTypes.bool,
  /**
   * Text content of the component.
   */
  children: PropTypes.node,
  className: PropTypes.string,
  /**
   * Color of the text.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success', 'warning', 'info', 'dark', 'neutral-1', 'neutral-2', 'neutral-3', 'neutral-4', 'neutral-5', 'neutral-6', 'neutral-7', 'neutral-8', 'neutral-9', 'neutral-10']),
  fontStyle: PropTypes.oneOf(['normal', 'italic']),
  fontWeight: PropTypes.oneOf(['normal', 'semi-bold']),
  id: PropTypes.string,
  lineHeight: PropTypes.oneOf(['xs', 'sm', 'md']),
  lines: PropTypes.number,
  size: PropTypes.oneOfType([PropTypes.oneOf(TEXT_SIZE), PropTypes.shape({
    xs: PropTypes.oneOf(TEXT_SIZE),
    sm: PropTypes.oneOf(TEXT_SIZE),
    md: PropTypes.oneOf(TEXT_SIZE),
    lg: PropTypes.oneOf(TEXT_SIZE),
    xl: PropTypes.oneOf(TEXT_SIZE)
  })]),
  title: PropTypes.string,
  transform: PropTypes.oneOf(['uppercase', 'lowercase', 'capitalize']),
  truncate: PropTypes.bool,
  whiteSpace: PropTypes.oneOf(['nowrap'])
};

export { Text };
